import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import {
  AccountReportFilterActions,
  AccountReportPageHeader,
  AccountReportTableShell,
} from "@/components/accounts/AccountReportShell";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { formatReportDate, money } from "@/lib/accounts/ledgerReportUtils";
import {
  deleteManualVoucher,
  listManualVouchers,
  listVoucherTypes,
  type ManualVoucherRecord,
  type VoucherTypeOption,
} from "@/lib/accounts/manualVoucherStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { isAxiosError } from "axios";
import { FileText, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

function voucherErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string" && data.message.trim()) return data.message;
  }
  return "Could not load vouchers.";
}

const Vouchers = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "chart_of_account_can_view_any") || can(user, "bank_can_view_any");
  const [isForbidden, setIsForbidden] = useState(false);

  const [voucherTypes, setVoucherTypes] = useState<VoucherTypeOption[]>([]);
  const [voucherTypeCode, setVoucherTypeCode] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [loading, setLoading] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [items, setItems] = useState<ManualVoucherRecord[]>([]);
  const [meta, setMeta] = useState({ currentPage: 1, lastPage: 1, perPage: 20, total: 0 });
  const [page, setPage] = useState(1);

  const [deleteTarget, setDeleteTarget] = useState<ManualVoucherRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  const currencySymbol = user?.company_branding?.currency?.symbol?.trim() || "";

  const activeFilters = useMemo(
    () => ({
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
      voucherTypeCode: voucherTypeCode || null,
    }),
    [dateFrom, dateTo, voucherTypeCode],
  );

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void listVoucherTypes()
      .then((rows) => {
        if (!cancelled) setVoucherTypes(rows);
      })
      .catch(() => {
        if (!cancelled) toast.error("Could not load voucher types.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const applyFilters = useCallback(
    async (nextPage = 1) => {
      if (!canView) return;
      setLoading(true);
      setIsForbidden(false);
      try {
        const result = await listManualVouchers(nextPage, 20, activeFilters);
        setItems(result.items);
        setMeta(result.meta);
        setPage(result.meta.currentPage);
        setHasApplied(true);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        toast.error(voucherErrorMessage(error));
      } finally {
        setLoading(false);
      }
    },
    [canView, activeFilters],
  );

  const resetFilters = () => {
    setVoucherTypeCode("");
    setDateFrom("");
    setDateTo("");
    setItems([]);
    setMeta({ currentPage: 1, lastPage: 1, perPage: 20, total: 0 });
    setPage(1);
    setHasApplied(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteManualVoucher(deleteTarget.id);
      toast.success("Voucher deleted.");
      setDeleteTarget(null);
      await applyFilters(page);
    } catch (error) {
      toast.error(voucherErrorMessage(error));
    } finally {
      setDeleting(false);
    }
  };

  if (!canView || isForbidden) {
    return (
      <Layout>
        <PermissionDenied />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-5">
        <AccountReportPageHeader
          icon={<FileText className="h-5 w-5 text-primary" />}
          title="Vouchers"
          description="Browse posted manual vouchers. Filter by type and date range, or create a new double-entry voucher."
          actions={
            <Button asChild variant="premium" size="sm" className="h-8 text-xs">
              <Link to="/accounts/vouchers/new">
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                New voucher
              </Link>
            </Button>
          }
        />

        <div className="rounded-2xl border border-border/55 bg-card/80 p-4 shadow-sm ring-1 ring-border/25">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-semibold text-foreground">
                Voucher type <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Select
                value={voucherTypeCode || "__all__"}
                onValueChange={(v) => setVoucherTypeCode(v === "__all__" ? "" : v)}
              >
                <SelectTrigger className="glass-input h-9 text-xs">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All types</SelectItem>
                  {voucherTypes.map((opt) => (
                    <SelectItem key={opt.code} value={opt.code}>
                      {opt.code} — {opt.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">From date</Label>
              <DatePartsPicker
                value={dateFrom}
                onChange={setDateFrom}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                maxDate={dateTo ? new Date(`${dateTo}T00:00:00`) : undefined}
                placeholder="Select from date"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-foreground">To date</Label>
              <DatePartsPicker
                value={dateTo}
                onChange={setDateTo}
                fromYear={1980}
                toYear={new Date().getFullYear() + 1}
                minDate={dateFrom ? new Date(`${dateFrom}T00:00:00`) : undefined}
                placeholder="Select to date"
                align="end"
              />
            </div>
          </div>

          <AccountReportFilterActions
            hasApplied={hasApplied}
            loading={loading}
            showExport={false}
            onApply={() => void applyFilters(1)}
            onReset={resetFilters}
            statusText={
              hasApplied ? (
                <>
                  Showing{" "}
                  <span className="font-semibold text-foreground">{meta.total}</span> voucher
                  {meta.total === 1 ? "" : "s"}
                  {voucherTypeCode ? (
                    <>
                      {" · Type "}
                      <span className="font-semibold text-foreground">{voucherTypeCode}</span>
                    </>
                  ) : null}
                  {dateFrom ? (
                    <>
                      {" · From "}
                      <span className="font-semibold text-foreground">{formatReportDate(dateFrom)}</span>
                    </>
                  ) : null}
                  {dateTo ? (
                    <>
                      {" · To "}
                      <span className="font-semibold text-foreground">{formatReportDate(dateTo)}</span>
                    </>
                  ) : null}
                </>
              ) : (
                "Optionally filter by voucher type and dates, then Apply filter."
              )
            }
          />
        </div>

        <AccountReportTableShell
          loading={loading}
          hasApplied={hasApplied}
          notAppliedMessage="No vouchers loaded yet. Apply filter to view posted vouchers."
        >
          {items.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No vouchers found for this filter.
            </p>
          ) : (
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date / Voucher #</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Narration</TableHead>
                    <TableHead className="text-right">
                      Debit{currencySymbol ? ` (${currencySymbol})` : ""}
                    </TableHead>
                    <TableHead className="text-right">
                      Credit{currencySymbol ? ` (${currencySymbol})` : ""}
                    </TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="min-w-[8rem] whitespace-nowrap">
                        <div className="text-sm text-foreground">{formatReportDate(item.date)}</div>
                        <div className="font-mono text-[11px] text-muted-foreground">
                          {item.voucherNumber || "—"}
                        </div>
                      </TableCell>
                      <TableCell className="min-w-[5rem]">
                        {item.voucherTypeCode ? (
                          <span className="rounded border border-border/40 bg-muted/40 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide">
                            {item.voucherTypeCode}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="min-w-[10rem] max-w-[22rem] text-sm text-muted-foreground">
                        {item.description || "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                        {money(item.totalDebit, true)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-rose-700 dark:text-rose-400">
                        {money(item.totalCredit, true)}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleteTarget(item)}
                          aria-label="Delete voucher"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {meta.lastPage > 1 ? (
                <div className="flex items-center justify-between gap-2 border-t border-border/40 px-4 py-3">
                  <p className="text-xs text-muted-foreground">
                    Page {meta.currentPage} of {meta.lastPage}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      disabled={page <= 1 || loading}
                      onClick={() => void applyFilters(page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      disabled={page >= meta.lastPage || loading}
                      onClick={() => void applyFilters(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </AccountReportTableShell>
      </div>

      <ConfirmAlertDialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
        title="Delete voucher?"
        description={
          deleteTarget
            ? `This will remove ${deleteTarget.voucherNumber || "this voucher"} and reverse its journal entry.`
            : ""
        }
        confirmLabel={deleting ? "Deleting…" : "Delete"}
        onConfirm={() => {
          if (!deleting) void handleDelete();
        }}
      />
    </Layout>
  );
};

export default Vouchers;
